import { faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Big from 'big.js';
import { format } from 'd3-format';
import React from 'react';
import styled from 'styled-components';

import { APIConfig } from 'src/config';
import {
  COLOR_EXPORT, COLOR_EXPORT_UNTRADED,
  COLOR_IMPORT, COLOR_IMPORT_UNTRADED,
} from 'src/enosikit/components/Chart/components/chartConstants';
import {
  BUY, COMMUNITY, CONTRACTED, COST_BENEFIT_STATE_ICONS, NA_SYMBOL, NOMINATED,
  PLATFORM_MODE_REBATE, RESIDUAL, SELL, TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
  TRADE_TYPE_RESIDUAL, UNTRADED_ENERGY_KEY,
} from 'src/util/constants';
import { costBenefitLabel, netValueDiffState } from 'src/util/counterfactual';
import { useIntlCurrency } from 'src/util/i18n/currency';
import { i18nDecimalFormat } from 'src/util/i18n/handler';
import logger from 'src/util/logger';
import { isNumber } from 'src/util/math';

export const CardsLabel = styled.p.attrs({
  className: 'mb-2',
})`
  color: #adb5bd;
  font-size: 0.8em;
  margin: 0;
  padding: 0
`;

export const CostBenefitText = styled.span.attrs({
  className: 'ms-2',
})`
font-size: 0.8rem;
`;

/**
 * Returns the avatar shown as part of the trade and meter cards
 * @param {object} user
 * @param {string} ring
 * @param {NOMINATED | CONTRACTED | COMMUNITY | RESIDUAL} tradeType
 * @returns {React.ReactElement} - avatar used in meter and trade cards
 */
export const avatar = (user, ring, tradeType) => {
  const baseStyle = {
    background: '#fff', borderRadius: '50%', height: '4em', width: '4em',
  };

  let avatarImage;
  if (user || tradeType === NOMINATED) {
    avatarImage = (
      <div className="property-card-user" style={{ ...baseStyle, background: '#fff' }}>
        <FontAwesomeIcon icon={faUserCircle} size="1x" style={{ height: '100%', width: '100%' }} />
      </div>
    );
  } else {
    avatarImage = <div style={{ ...baseStyle, background: '#fff' }} />;
  }
  return (
    <div style={{ borderRadius: '50%', padding: '0.25rem', background: ring }}>
      {avatarImage}
    </div>
  );
};

/**
 * Determines the card opacity based on the user selection
 * @param {string} key - normally tradeid or a combination of trade and meterid
 * @param {Array} hoverKeys
 * @param {Array} selectedKeys
 * @returns {string} - opacity for the cards.
 */
export const opacity = (key, hoverKeys, selectedKeys) => {
  // Default state
  if (hoverKeys.length === 0 && selectedKeys.length === 0) { return '100%'; }
  // Hovering
  if (hoverKeys.includes(key)) { return '100%'; }
  // Selected - with and without hovering
  if (selectedKeys.includes(key)) { return hoverKeys.length === 0 ? '100%' : '40%'; }
  // Not selected
  return '10%';
};

/**
 * Ring builds the ring details around the element.
 * @param {object} buy
 * @param {number} buy.count
 * @param {object} sell
 * @param {number} sell.count
 * @param {boolean} isUntraded
 * @returns {string} the CSS string for the ring.
 */
export const ring = (buy = { count: 0 }, sell = { count: 0 }, isUntraded = false) => {
  if (!buy && !sell) { return 'none'; }

  const buyColor = isUntraded ? COLOR_IMPORT_UNTRADED : COLOR_IMPORT;
  const sellColor = isUntraded ? COLOR_EXPORT_UNTRADED : COLOR_EXPORT;

  if (buy?.count > 0 && sell?.count > 0) { return `linear-gradient(to right, ${buyColor} 50%, ${sellColor} 50%)`; }
  if (buy?.count > 0) { return buyColor; }
  if (sell?.count > 0) { return sellColor; }

  return 'none';
};

/**
 * Provides the meter identifier for the meter label
 * @param {string} title
 * @param {string} identifier
 * @returns {string} - meter identifier.
 */
export const meterIdentifier = (title, identifier) => {
  if (!identifier) {
    return '';
  }

  return title ? ` (${identifier})` : identifier;
};

/**
 * Returns internationalized header labels for chart cards based on trade direction.
 * Returns "Exports" for SELL direction, "Imports" for BUY direction, and throws an error for
 * unknown directions.
 * @param {import('react-intl').IntlShape} intl - The react-intl formatMessage function for
 *   internationalization
 * @param {BUY |SELL} direction - Trade direction
 * @returns {string} - Formatted string message: "Exports" for SELL, "Imports" for BUY, and throws
 * an error for unknown directions.
 */
export const getDirectionHeaderLabel = (intl, direction) => {
  switch (direction) {
    case SELL:
      return intl.formatMessage({ id: 'chart.helpers.cards.get_card_header_label.exports.label', defaultMessage: 'Exports' });
    case BUY:
      return intl.formatMessage({ id: 'chart.helpers.cards.get_card_header_label.imports.label', defaultMessage: 'Imports' });
    default:
      logger.error(`'${direction}' is not valid `);
      throw new Error(`direction '${direction}' is not valid`);
  }
};

/**
 * Build and return the content for the meter cards.
 * @param {import('react-intl').IntlShape} intl - The intl object for internationalization
 * @param {React.ReactElement} cardLabel - cards label
 * @param {number} [buyVolume=0]
 * @param {number} [sellVolume=0]
 * @returns {React.ReactElement} - meter data container.
 */
export const meterContent = (intl, cardLabel, buyVolume = 0, sellVolume = 0) => (
  <div className="mb-2">
    <h5>{cardLabel}</h5>
    <h6 className="mb-2">
      {getDirectionHeaderLabel(intl, BUY)}
    </h6>
    <div className="mb-2">{`${i18nDecimalFormat(format('.4s')(buyVolume), intl.locale)}Wh`}</div>
    <h6>
      {getDirectionHeaderLabel(intl, SELL)}
    </h6>
    <div className="mb-2">{`${i18nDecimalFormat(format('.4s')(sellVolume), intl.locale)}Wh`}</div>
  </div>
);

/**
 * Formats the cost/benefit of a card's trades compared with their counterfactual, as a
 * stock-ticker style icon and an abbreviation describing the cost or benefit.
 * @param {import('react-intl').IntlShape} intl
 * @param {object} intlCurrency - i18n currency object
 * @param {BUY | SELL} dir - trade direction of the card details.
 * @param {object} dirDatum - the card's details for that direction.
 * @param {number} dirDatum.count - number of data points summed into the card.
 * @param {number} dirDatum.value - traded value.
 * @param {number} dirDatum.counterfactual - counterfactual value (NaN when incomplete).
 * @returns {React.ReactElement | null} the cost/benefit indicator, or null when unavailable.
 */
const formatCardCostBenefit = (intl, intlCurrency, dir, dirDatum) => {
  const { count, counterfactual, value } = dirDatum || {};
  if (!count || !isNumber(value) || !isNumber(counterfactual)) {
    return null;
  }

  const netValueDiff = Number(Big(value).minus(counterfactual));
  const absoluteValueDiff = Math.abs(netValueDiff);
  const direction = dir === BUY ? TRADE_DIRECTION_BUY : TRADE_DIRECTION_SELL;
  const diffState = netValueDiffState(netValueDiff, direction);
  const finalDiffState = Object.keys(diffState).find((k) => diffState[k]);

  return (
    <CostBenefitText data-testid={`cardCostBenefit-${dir}`}>
      <span className="me-1">{COST_BENEFIT_STATE_ICONS[finalDiffState]}</span>
      <abbr title={costBenefitLabel(intl, intlCurrency, absoluteValueDiff, finalDiffState)}>
        {intlCurrency.formatMessage(
          { id: 'common.currency.value', defaultMessage: '{n, number, :: .00 currency/AUD}' },
          { n: absoluteValueDiff },
        )}
      </abbr>
    </CostBenefitText>
  );
};

/**
 * Build and return the content for trade cards.
 * @param {import('react-intl').IntlShape} intl - The intl object for internationalization
 * @param {object} cardDatum The card's datum
 * @param {string} cardDatum.tradeType The card's trade type (or untraded energy key).
 * @param {object} cardDatum.buy The card's buy details
 * @param {object} cardDatum.buy.value The card's buy details
 * @param {object} cardDatum.buy.volume The card's buy details
 * @param {object} cardDatum.buy.counterfactual The card's buy counterfactual
 * @param {object} cardDatum.sell The card's buy details
 * @param {object} cardDatum.sell.value The card's buy details
 * @param {object} cardDatum.sell.volume The card's buy details
 * @param {object} cardDatum.sell.counterfactual The card's sell counterfactual
 * @param {React.ReactElement} cardLabel The label UI for the cards
 * @param {{buy: boolean, sell: boolean}} [counterfactualAvailability] Direction-specific
 *   counterfactual availability for the property or portfolio.
 * @returns {React.ReactElement} - trade data container.
 */
export const tradeContent = (intl, cardDatum, cardLabel, counterfactualAvailability = {}) => {
  const discountAmountLabel = intl.formatMessage({
    id: 'chart.helpers.cards.trade_content.discount_amount',
    defaultMessage: 'Discount Amount',
  });
  const intlCurrency = useIntlCurrency();
  const { tradeType } = cardDatum;
  const isResidual = tradeType === TRADE_TYPE_RESIDUAL;
  const showsCostBenefit = !isResidual && tradeType !== UNTRADED_ENERGY_KEY;

  return (
    <>
      <h5>{cardLabel}</h5>
      {[BUY, SELL].map((dir) => {
        if (APIConfig().MODE === PLATFORM_MODE_REBATE) {
          return (
            <React.Fragment key={dir}>
              <h6>{getDirectionHeaderLabel(intl, dir)}</h6>
              <div className="mb-2">
                {`${i18nDecimalFormat(format('.4s')(cardDatum[dir].volume), intl.locale)}Wh`}
              </div>
              {!isResidual && (
                <>
                  <h6>{discountAmountLabel}</h6>
                  <div className="mb-2">
                    {intlCurrency.formatMessage(
                      { id: 'common.currency.value', defaultMessage: '{n, number, :: .00 currency/AUD}' },
                      { n: cardDatum[dir].value },
                    )}
                  </div>
                </>
              )}
            </React.Fragment>
          );
        }
        return (
          <React.Fragment key={dir}>
            <h6>{getDirectionHeaderLabel(intl, dir)}</h6>
            <div className="mb-2">
              { (cardDatum[dir].value === 0
                  || (cardDatum[dir].value && isNumber(cardDatum[dir].value)))
                ? intlCurrency.formatMessage(
                  { id: 'common.currency.value', defaultMessage: '{n, number, :: .00 currency/AUD}' },
                  { n: cardDatum[dir].value },
                ) : NA_SYMBOL}
              {showsCostBenefit && counterfactualAvailability?.[dir]
                && formatCardCostBenefit(intl, intlCurrency, dir, cardDatum[dir])}
            </div>
            <div className="mb-2">
              {`${i18nDecimalFormat(format('.4s')(cardDatum[dir].volume), intl.locale)}Wh`}
            </div>
          </React.Fragment>
        );
      })}
    </>
  );
};
